import { addDays, addMinutes, addWeeks, differenceInMinutes } from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";

/**
 * Every instant in the system is a UTC `Date` backed by a `timestamptz` column. Anything that
 * reasons about a weekday, a start time, a calendar day, or a month has to do it in the business's
 * timezone, so it goes through this module. V1 is fixed to Asia/Dubai but the timezone is a
 * parameter everywhere, so the day a business outside the UAE signs up nothing has to be rewritten.
 */
export const DEFAULT_TIMEZONE = "Asia/Dubai";

/**
 * Short month names, fixed. Never `toLocaleString(..., { month: "short" })` for them: the ICU data
 * changes between runtimes (newer en-GB data gives "Sept"), so the text differs by machine.
 */
export const MONTH_SHORT: readonly string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "1 Sep 2026" for a `yyyy-MM-dd` key. Calendar days have no time zone. */
export function formatDayKey(key: string): string {
  const [year, month, day] = key.split("-").map(Number);
  return `${day} ${MONTH_SHORT[month! - 1]} ${year}`;
}

export { addDays, addMinutes, addWeeks, differenceInMinutes };

/** `HH:mm` or `HH:mm:ss`, matching a Postgres `time` column. */
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
/** `yyyy-MM-dd`, matching a Postgres `date` column. */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export class TimeError extends Error {}

export function isLocalDate(value: string): boolean {
  return DATE_PATTERN.test(value);
}

export function isLocalTime(value: string): boolean {
  return TIME_PATTERN.test(value);
}

/** Calendar day in the given timezone, as `yyyy-MM-dd`. */
export function localDate(instant: Date, timeZone: string = DEFAULT_TIMEZONE): string {
  return formatInTimeZone(instant, timeZone, "yyyy-MM-dd");
}

/** Wall clock time in the given timezone, as `HH:mm`. */
export function localTime(instant: Date, timeZone: string = DEFAULT_TIMEZONE): string {
  return formatInTimeZone(instant, timeZone, "HH:mm");
}

/** Day of week in the given timezone, 0 for Sunday through 6 for Saturday, matching Postgres `dow`. */
export function localWeekday(instant: Date, timeZone: string = DEFAULT_TIMEZONE): number {
  return toZonedTime(instant, timeZone).getDay();
}

/** Month bucket in the given timezone, as `yyyy-MM`. Used to count reschedules per month. */
export function localMonthKey(instant: Date, timeZone: string = DEFAULT_TIMEZONE): string {
  return formatInTimeZone(instant, timeZone, "yyyy-MM");
}

/**
 * Turn a local calendar date plus a local wall clock time into the UTC instant it refers to.
 * This is how a schedule template ("Tuesdays at 17:00") becomes a concrete session row.
 */
export function zonedDateTime(
  date: string,
  time: string,
  timeZone: string = DEFAULT_TIMEZONE,
): Date {
  if (!isLocalDate(date)) throw new TimeError(`Not a yyyy-MM-dd date: ${date}`);
  const match = TIME_PATTERN.exec(time);
  if (!match) throw new TimeError(`Not a HH:mm[:ss] time: ${time}`);
  const seconds = match[3] ?? "00";
  return fromZonedTime(`${date}T${match[1]}:${match[2]}:${seconds}`, timeZone);
}

/** Midnight at the start of the instant's local calendar day, as a UTC instant. */
export function startOfLocalDay(instant: Date, timeZone: string = DEFAULT_TIMEZONE): Date {
  return zonedDateTime(localDate(instant, timeZone), "00:00", timeZone);
}

/** Midnight at the start of the next local calendar day, as a UTC instant. */
export function endOfLocalDay(instant: Date, timeZone: string = DEFAULT_TIMEZONE): Date {
  return addDays(startOfLocalDay(instant, timeZone), 1);
}

/** Half-open range `[start, end)` covering the instant's local month. */
export function localMonthRange(
  instant: Date,
  timeZone: string = DEFAULT_TIMEZONE,
): { start: Date; end: Date } {
  const monthKey = localMonthKey(instant, timeZone);
  const start = zonedDateTime(`${monthKey}-01`, "00:00", timeZone);
  const [yearPart, monthPart] = monthKey.split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  const end = zonedDateTime(
    `${nextYear}-${String(nextMonth).padStart(2, "0")}-01`,
    "00:00",
    timeZone,
  );
  return { start, end };
}

/**
 * The next local calendar date on or after `from` that falls on `weekday` (0 for Sunday).
 * Returned as a `yyyy-MM-dd` string, because callers pair it with a template's `start_time`.
 */
export function nextLocalDateOnWeekday(
  from: Date,
  weekday: number,
  timeZone: string = DEFAULT_TIMEZONE,
): string {
  if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) {
    throw new TimeError(`Weekday must be an integer 0 through 6, got ${weekday}`);
  }
  const offset = (weekday - localWeekday(from, timeZone) + 7) % 7;
  return localDate(addDays(from, offset), timeZone);
}

/** Whole and fractional hours from `now` until `target`. Negative once the target has passed. */
export function hoursUntil(now: Date, target: Date): number {
  return differenceInMinutes(target, now) / 60;
}

/** Human copy for notifications and policy explanations, for example "Tue 2 Sep at 17:00". */
export function formatSessionTime(instant: Date, timeZone: string = DEFAULT_TIMEZONE): string {
  return formatInTimeZone(instant, timeZone, "EEE d MMM 'at' HH:mm");
}

/** The day of a session, for example "Mon 12 Oct": the batch booking confirmation lists days. */
export function formatSessionDay(instant: Date, timeZone: string = DEFAULT_TIMEZONE): string {
  return formatInTimeZone(instant, timeZone, "EEE d MMM");
}

/** Human copy for a due date, for example "2 September 2026". */
export function formatDueDate(instant: Date, timeZone: string = DEFAULT_TIMEZONE): string {
  return formatInTimeZone(instant, timeZone, "d MMMM yyyy");
}

/**
 * The first day of a business week, as in `Date.getDay`: 6 is Saturday. The setup weekday toggles,
 * the owner week (phone and desktop) and the mobile app all start their week here, so "This week"
 * names the same seven days on every screen. Change it here and nowhere else.
 */
export const WEEK_START_DAY = 6;

/** The weekdays in business-week order (`Date.getDay` numbers), for weekday toggles and headers. */
export const WEEK_ORDER: readonly number[] = Array.from({ length: 7 }, (_, index) => (WEEK_START_DAY + index) % 7);

/** Moves a `yyyy-MM-dd` calendar key by whole days. Calendar arithmetic, so no clock change can shift it. */
export function shiftDateKey(key: string, byDays: number): string {
  if (!isLocalDate(key)) throw new TimeError(`Not a yyyy-MM-dd date: ${key}`);
  const [year, month, day] = key.split("-").map(Number);
  return new Date(Date.UTC(year!, month! - 1, day! + byDays)).toISOString().slice(0, 10);
}

/**
 * The seven `yyyy-MM-dd` keys of the business week that holds `todayKey`, moved by `weekOffset`
 * whole weeks. `todayKey` is today in the business's timezone (`localDate(now, timezone)`), never the
 * device's, so a laptop abroad sees the same week as the pool deck.
 */
export function businessWeekKeys(todayKey: string, weekOffset = 0, weekStart: number = WEEK_START_DAY): string[] {
  const weekday = new Date(`${todayKey}T00:00:00.000Z`).getUTCDay();
  const back = (weekday - weekStart + 7) % 7;
  const start = shiftDateKey(todayKey, -back + weekOffset * 7);
  return Array.from({ length: 7 }, (_, index) => shiftDateKey(start, index));
}

/**
 * The business week that holds `now`, moved by `weekOffset` whole weeks: its seven calendar keys and
 * the half-open range `[from, to)` from its first local midnight to the one after its last day. The
 * clock is the business's, so a phone set to another timezone still asks for the same week.
 */
export function businessWeekRange(
  now: Date,
  timeZone: string = DEFAULT_TIMEZONE,
  weekOffset = 0,
  /** The first day of the week, as in `Date.getDay`. The studio numbers (H4) start on Monday (1). */
  weekStart: number = WEEK_START_DAY,
): { keys: string[]; from: Date; to: Date } {
  const keys = businessWeekKeys(localDate(now, timeZone), weekOffset, weekStart);
  return {
    keys,
    from: zonedDateTime(keys[0]!, "00:00", timeZone),
    to: zonedDateTime(shiftDateKey(keys[6]!, 1), "00:00", timeZone),
  };
}
